/**
 * מסך יומן פעולות — TASKS 3.6, PRD 8.2: "audit log לצפייה בלבד".
 *
 * ⛔ ⛔ **מסך קריאה בלבד במכוון** — אין כאן שום פעולת כתיבה, לא עריכה ולא
 *    מחיקה. זה בדיוק ההבדל בין יומן ביקורת אמיתי לרשימה רגילה.
 */
import { Fragment, useEffect, useState } from 'react';
import { AdminNav } from './AdminNav.js';
import { AdminPageHeader } from './AdminPageHeader.js';
import { isAdminApiError } from './adminApi.js';
import { listAuditLog, type AuditLogEntry } from './auditLogApi.js';

const ACTION_LABEL: Record<string, string> = {
  create: 'יצירה',
  update: 'עדכון',
  delete: 'מחיקה',
  login: 'התחברות',
  login_failed: 'ניסיון התחברות כושל',
};

// ⚠️ רשימה סטטית — הישויות שכותבות ל-audit_log בפועל נכון ל-3.6 (categories,
//    products, product_variants, product_media, admin_users). ⛔ **אין
//    `settings`** — מסך ההגדרות הוסר במכוון (11.08.2026, ראו TASKS 3.6):
//    פרטי החברה כמעט-קבועים בעסק יציב, שינוי הוא עריכה ישירה ב-DB.
//    ⛔ לא נגזרת דינמית מה-DB — תוספת ישות חדשה מחייבת עדכון כאן.
const ENTITY_OPTIONS = [
  'categories',
  'products',
  'product_variants',
  'product_media',
  'admin_users',
];

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'medium' });
}

export function AuditLogPage() {
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [entity, setEntity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    setError(null);
    listAuditLog({ page, entity: entity || undefined })
      .then((result) => {
        setEntries(result.entries);
        setTotal(result.total);
        setPageSize(result.pageSize);
      })
      .catch((err: unknown) => {
        setError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת היומן.');
      });
  }, [page, entity]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <AdminNav />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <AdminPageHeader
          title="יומן פעולות"
          description="תיעוד קריאה-בלבד של כל פעולות היצירה, העדכון והמחיקה בפאנל."
        />

        <div className="mt-6 max-w-xs">
          <label htmlFor="log-entity" className="block text-sm font-semibold">
            ישות
          </label>
          <select
            id="log-entity"
            value={entity}
            onChange={(e) => {
              setPage(1);
              setEntity(e.target.value);
            }}
            className="mt-1 w-full rounded-md border border-line px-3 py-2"
          >
            <option value="">הכול</option>
            {ENTITY_OPTIONS.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <p role="alert" className="text-danger mt-4 text-sm">
            {error}
          </p>
        )}

        {entries === null && !error && <p className="text-muted mt-6">טוען…</p>}
        {entries && entries.length === 0 && <p className="text-muted mt-6">אין רשומות.</p>}

        {entries && entries.length > 0 && (
          <>
            <table className="admin-table mt-6 w-full border-collapse text-sm">
              <caption className="sr-only">
                יומן פעולות, עמוד {page} מתוך {totalPages}
              </caption>
              <thead>
                <tr className="border-line border-b text-start">
                  <th scope="col" className="p-2 text-start">
                    זמן
                  </th>
                  <th scope="col" className="p-2 text-start">
                    מבצע
                  </th>
                  <th scope="col" className="p-2 text-start">
                    פעולה
                  </th>
                  <th scope="col" className="p-2 text-start">
                    ישות
                  </th>
                  <th scope="col" className="p-2 text-start">
                    מזהה
                  </th>
                  <th scope="col" className="p-2 text-start">
                    פרטים
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <Fragment key={entry.id}>
                    <tr className="border-line border-b">
                      <td className="tabular p-2">{formatTimestamp(entry.created_at)}</td>
                      <td className="p-2">{entry.actor}</td>
                      <td className="p-2">
                        {/* ⭐ DESIGN.md 8.3 — "chips... רקע surface + טקסט ink,
                            לא צבע לבד" - נייטרלי, לא מבוסס-צבע לפי סוג. */}
                        <span className="bg-surface border-line inline-block rounded-full border px-2 py-0.5 text-xs font-medium">
                          {ACTION_LABEL[entry.action] ?? entry.action}
                        </span>
                      </td>
                      <td className="p-2">{entry.entity}</td>
                      <td className="p-2">
                        <span className="ltr-inline">{entry.entity_id ?? '-'}</span>
                      </td>
                      <td className="p-2">
                        <button
                          type="button"
                          className="rounded border border-line px-2 py-1"
                          aria-expanded={expandedId === entry.id}
                          onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                        >
                          {expandedId === entry.id ? 'סגירה' : 'פרטים'}
                        </button>
                      </td>
                    </tr>
                    {expandedId === entry.id && (
                      <tr className="border-line border-b">
                        <td colSpan={6} className="p-2">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <p className="text-xs font-semibold">לפני</p>
                              <pre className="mt-1 overflow-x-auto rounded-md bg-surface p-2 text-xs">
                                {entry.before_json
                                  ? JSON.stringify(entry.before_json, null, 2)
                                  : '-'}
                              </pre>
                            </div>
                            <div>
                              <p className="text-xs font-semibold">אחרי</p>
                              <pre className="mt-1 overflow-x-auto rounded-md bg-surface p-2 text-xs">
                                {entry.after_json ? JSON.stringify(entry.after_json, null, 2) : '-'}
                              </pre>
                            </div>
                          </div>
                          {entry.ip && (
                            <p className="text-muted ltr-inline mt-2 text-xs">IP: {entry.ip}</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>

            <nav aria-label="עימוד יומן פעולות" className="mt-4 flex items-center gap-3">
              <button
                type="button"
                className="rounded border border-line px-3 py-1 disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                הקודם
              </button>
              <span className="text-sm">
                עמוד {page} מתוך {totalPages} ({total} רשומות)
              </span>
              <button
                type="button"
                className="rounded border border-line px-3 py-1 disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                הבא
              </button>
            </nav>
          </>
        )}
      </main>
    </>
  );
}
